import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import type { ReactNode } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useAuth0 } from "@auth0/auth0-react"
import { Toast } from "primereact/toast"
import { Button } from "primereact/button"
import { useCreateDraft, useMyDrafts, useWorkItemAction, useWorkItemDetail } from "api/useWorkItems"
import { useCustomerData } from "context/customer-provider"
import useAccess from "api/useAccess"
import { isWorkPackagesHidden } from "utils/workPackagesHidden"
import DraftTray from "components/DraftTray"
import type { AddItemsResponse, WorkItem, WorkItemDetail } from "types/work-item"

// What a caller gets back after adding to (or seeding) a draft, so it can build
// its own toast copy + Undo action against the exact ids that landed.
export type DraftAddResult = {
    publicId: string
    title: string
    addedIds: string[]
    addedCount: number
    alreadyInDraft: number
    // Ids the backend no longer recognises (deleted since the list rendered).
    unknownCount: number
    draftCount: number
}

type ToastAction = { label: string; icon?: string; onClick: () => void }

export type DraftToast = {
    severity?: "success" | "info" | "warn"
    detail: string
    actions?: ToastAction[]
    life?: number
}

type DraftPackageContextValue = {
    // Feature flag AND roads product — every draft surface checks this.
    enabled: boolean
    // enabled AND the user may change data (not view only): the surfaces that
    // CREATE or edit packages check this; read-only package state (the "In
    // Repairs" banner, membership chips) stays on enabled.
    canCreate: boolean
    activeDraftId: string | null
    activeDraft: WorkItemDetail | null
    // subjectIds currently in the active draft (drives the detail "✓ In {title}").
    draftItemIds: Set<string>
    drafts: WorkItem[]
    isBusy: boolean
    // Which of MY drafts a detection is collected in (first match), for the
    // membership chips on list rows and the detail page. Null when un-collected.
    draftFor: (detectionId: string | number) => { publicId: string; title: string } | null
    // Add detections to the active draft, lazily creating one if none is active.
    // sourceEl (optional, all three): the clicked control — a ghost flies from
    // it into the tray pill so the user sees WHERE the defects went.
    addToActiveDraft: (detections: { id: string }[], sourceEl?: HTMLElement | null) => Promise<DraftAddResult | null>
    // Add detections to a specific draft, making it the active one.
    addToDraft: (publicId: string, detections: { id: string }[], sourceEl?: HTMLElement | null) => Promise<DraftAddResult | null>
    // Always start a fresh draft and add the detections to it.
    createDraftWith: (detections: { id: string }[], sourceEl?: HTMLElement | null) => Promise<DraftAddResult | null>
    // The tray pill registers its DOM node here (the fly-to target) and re-renders
    // on trayPulseNonce to play its arrival pulse.
    registerTrayPill: (el: HTMLElement | null) => void
    trayPulseNonce: number
    setActiveDraft: (publicId: string | null) => void
    // Cancel the active draft server-side and stop tracking it.
    discardActiveDraft: () => Promise<void>
    removeFromActiveDraft: (detectionId: string) => Promise<void>
    // Remove specific detections from a specific draft (drives toast "Undo").
    removeItemsFromDraft: (publicId: string, detectionIds: string[]) => Promise<void>
    notify: (toast: DraftToast) => void
    // Ask the tray to expand and focus its title input (the "Rename" affordance).
    requestTitleFocus: () => void
    titleFocusNonce: number
    // Height (px) of the DV2 batch bar when visible, so the globally-mounted tray
    // can float ~12px above it instead of overlapping. 0 when no bar is showing.
    batchBarHeight: number
    setBatchBarHeight: (height: number) => void
}

const DraftPackageContext = createContext<DraftPackageContextValue | undefined>(undefined)

export const useDraftPackage = (): DraftPackageContextValue => {
    const ctx = useContext(DraftPackageContext)
    if (!ctx) throw new Error("useDraftPackage must be used within a DraftPackageProvider")
    return ctx
}

const storageKey = (customerId: string | undefined) => `rr:activeDraft:${customerId ?? ""}`

// Default draft name, e.g. "New package · 8 Jul".
const defaultDraftName = () => `New package · ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`

const byCreatedDesc = (a: WorkItem, b: WorkItem) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()

// Fly a small ghost card from the clicked control into the tray pill (the
// add-to-cart pattern): a gentle upward arc, scaling down and fading as it
// lands. Purely decorative — skipped entirely under prefers-reduced-motion
// (the pill's pulse/flash still confirms the add).
const flyGhost = (from: DOMRect, to: DOMRect) => {
    if (typeof window === "undefined" || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return
    const ghost = document.createElement("div")
    ghost.style.cssText = [
        "position:fixed",
        "z-index:2000",
        "pointer-events:none",
        `left:${from.left + from.width / 2 - 16}px`,
        `top:${from.top + from.height / 2 - 11}px`,
        "width:32px",
        "height:22px",
        "border-radius:6px",
        "background:#3b82f6",
        "box-shadow:0 2px 12px rgba(59,130,246,0.55)",
        "opacity:0.9",
    ].join(";")
    document.body.appendChild(ghost)
    const dx = to.left + to.width / 2 - (from.left + from.width / 2)
    const dy = to.top + to.height / 2 - (from.top + from.height / 2)
    const anim = ghost.animate(
        [
            { transform: "translate(0,0) scale(1)", opacity: 0.9 },
            // Arc apex: halfway across, lifted 70px above the straight line.
            { transform: `translate(${dx * 0.5}px, ${dy * 0.5 - 70}px) scale(0.65)`, opacity: 0.85, offset: 0.55 },
            { transform: `translate(${dx}px, ${dy}px) scale(0.25)`, opacity: 0.3 },
        ],
        { duration: 500, easing: "cubic-bezier(0.3, 0, 0.4, 1)" },
    )
    anim.onfinish = () => ghost.remove()
    // Safety net: never leave a stray ghost if the animation is interrupted.
    setTimeout(() => ghost.remove(), 800)
}

export const DraftPackageProvider = ({ children }: { children: ReactNode }) => {
    const { customerData, customerId } = useCustomerData()
    const isRoads = customerData?.product === "roads"
    // Default-ON for every roads customer; the hide-work-packages customer
    // feature (config.customer_feature, ART-3118) is the per-customer opt-out.
    const enabled = isRoads && !isWorkPackagesHidden(customerData)
    const { canEdit } = useAccess()
    const canCreate = enabled && canEdit

    const { user } = useAuth0()
    const actorName = user?.name || user?.email || undefined
    const actorEmail = user?.email || undefined
    const appBaseUrl = typeof window !== "undefined" ? window.location.origin : undefined

    const queryClient = useQueryClient()
    const createDraftMut = useCreateDraft()
    const action = useWorkItemAction()
    const draftsQuery = useMyDrafts(enabled)
    const drafts = useMemo(() => draftsQuery.data ?? [], [draftsQuery.data])

    const toastRef = useRef<Toast>(null)
    const [titleFocusNonce, setTitleFocusNonce] = useState(0)
    const [batchBarHeight, setBatchBarHeight] = useState(0)

    // Fly-to-tray: the pill registers its node; a successful add launches the
    // ghost and bumps the pulse nonce (the pill's arrival animation).
    const trayPillRef = useRef<HTMLElement | null>(null)
    const [trayPulseNonce, setTrayPulseNonce] = useState(0)
    const registerTrayPill = useCallback((el: HTMLElement | null) => {
        trayPillRef.current = el
    }, [])
    const celebrateAdd = useCallback((sourceEl?: HTMLElement | null) => {
        const target = trayPillRef.current
        if (sourceEl && target) {
            try {
                flyGhost(sourceEl.getBoundingClientRect(), target.getBoundingClientRect())
            } catch {
                // Decorative only — never let it break the add.
            }
        }
        // Land the pulse roughly when the ghost arrives (or immediately when
        // there's no flight).
        setTimeout(() => setTrayPulseNonce((n) => n + 1), sourceEl && target ? 420 : 0)
    }, [])

    const [activeDraftId, setActiveDraftId] = useState<string | null>(null)
    const { data: activeDraft } = useWorkItemDetail(enabled ? activeDraftId : null)

    const setActive = useCallback(
        (publicId: string | null) => {
            setActiveDraftId(publicId)
            if (typeof window === "undefined") return
            if (publicId) window.localStorage.setItem(storageKey(customerId), publicId)
            else window.localStorage.removeItem(storageKey(customerId))
        },
        [customerId],
    )

    // Load the persisted active id when the customer changes (or clear when the
    // feature is off for this customer).
    useEffect(() => {
        if (!enabled) {
            setActiveDraftId(null)
            return
        }
        const stored = typeof window !== "undefined" ? window.localStorage.getItem(storageKey(customerId)) : null
        setActiveDraftId(stored || null)
    }, [customerId, enabled])

    const notify = useCallback((toast: DraftToast) => {
        toastRef.current?.show({
            severity: toast.severity ?? "success",
            life: toast.life ?? 6000,
            content: (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px", flex: 1 }}>
                    <span style={{ fontSize: "0.9rem" }}>{toast.detail}</span>
                    {toast.actions && toast.actions.length > 0 && (
                        <div style={{ display: "flex", gap: "8px" }}>
                            {toast.actions.map((a) => (
                                <Button
                                    key={a.label}
                                    label={a.label}
                                    icon={a.icon}
                                    className="p-button-sm p-button-text"
                                    onClick={() => {
                                        toastRef.current?.clear()
                                        a.onClick()
                                    }}
                                />
                            ))}
                        </div>
                    )}
                </div>
            ),
        })
    }, [])

    // A failed gather/discard must never read as a silent no-op — surface it.
    const notifyFailure = useCallback(
        (detail: string) => notify({ severity: "warn", detail }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    )

    // Heal a stale active id: once a draft is dispatched or cancelled it drops out
    // of My Drafts, so fall back to the most recent remaining draft (or clear).
    // Only run against a settled list so a just-created id isn't clobbered.
    useEffect(() => {
        if (!enabled || draftsQuery.isFetching || !activeDraftId) return
        if (drafts.some((d) => d.public_id === activeDraftId)) return
        const fallbackDraft = drafts.length ? [...drafts].sort(byCreatedDesc)[0] : null
        setActive(fallbackDraft ? fallbackDraft.public_id : null)
        // The switch would otherwise be silent — the pill just changes what it
        // points at. Only worth a signal when there IS another draft to land on.
        if (fallbackDraft) notify({ severity: "info", detail: `Now adding to "${fallbackDraft.title}"` })
    }, [enabled, draftsQuery.isFetching, drafts, activeDraftId, setActive, notify])

    const draftItemIds = useMemo(() => {
        const ids = new Set<string>()
        for (const item of activeDraft?.items ?? []) ids.add(String(item.subjectId))
        return ids
    }, [activeDraft])

    // detectionId -> owning draft, across ALL my drafts (not just the active
    // one), so membership chips don't go blind when the active draft switches.
    const draftByDetectionId = useMemo(() => {
        const map = new Map<string, { publicId: string; title: string }>()
        for (const d of drafts) {
            const ids = ((d.payload as { draftDetectionIds?: string[] } | null)?.draftDetectionIds ?? []).map(String)
            for (const id of ids) if (!map.has(id)) map.set(id, { publicId: d.public_id, title: d.title })
        }
        return map
    }, [drafts])

    const draftFor = useCallback(
        (detectionId: string | number) => draftByDetectionId.get(String(detectionId)) ?? null,
        [draftByDetectionId],
    )

    const resolveTitle = useCallback(
        (publicId: string) => {
            const fromList = drafts.find((d) => d.public_id === publicId)?.title
            if (fromList) return fromList
            if (activeDraft?.public_id === publicId && activeDraft.title) return activeDraft.title
            return "Work package"
        },
        [drafts, activeDraft],
    )

    const createDraft = useCallback(async (): Promise<WorkItem> => {
        const res = await createDraftMut.mutateAsync({
            type: "work_package",
            draft: true,
            title: defaultDraftName(),
            assignerName: actorName,
            assignerEmail: actorEmail,
            appBaseUrl,
        })
        // Seed the drafts cache so switchers/the tray show it immediately and the
        // healing effect doesn't see it as stale before the refetch lands.
        queryClient.setQueryData<WorkItem[]>(["work-items", "drafts", customerId], (old = []) => [
            res.workItem,
            ...old.filter((d) => d.public_id !== res.workItem.public_id),
        ])
        setActive(res.workItem.public_id)
        return res.workItem
    }, [createDraftMut, actorName, actorEmail, appBaseUrl, queryClient, customerId, setActive])

    const addItems = useCallback(
        async (publicId: string, ids: string[]): Promise<AddItemsResponse> => {
            return (await action.mutateAsync({
                publicId,
                action: "add_items",
                payload: { detectionIds: ids },
                actorName,
                actorEmail,
            })) as AddItemsResponse
        },
        [action, actorName, actorEmail],
    )

    const toResult = (publicId: string, title: string, res: AddItemsResponse): DraftAddResult => ({
        publicId,
        title,
        addedIds: res.outcome.added,
        addedCount: res.outcome.added.length,
        alreadyInDraft: res.outcome.alreadyInDraft.length,
        unknownCount: res.outcome.unknown.length,
        draftCount: res.draftCount,
    })

    const addToActiveDraft = useCallback(
        async (detections: { id: string }[], sourceEl?: HTMLElement | null): Promise<DraftAddResult | null> => {
            const ids = detections.map((d) => String(d.id))
            if (!ids.length) return null
            try {
                let publicId = activeDraftId
                let title: string
                if (!publicId) {
                    const created = await createDraft()
                    publicId = created.public_id
                    title = created.title
                } else {
                    title = resolveTitle(publicId)
                }
                const res = await addItems(publicId, ids)
                celebrateAdd(sourceEl)
                return toResult(publicId, title, res)
            } catch {
                notifyFailure("Couldn't add to the package — please try again.")
                return null
            }
        },
        [activeDraftId, createDraft, resolveTitle, addItems, celebrateAdd, notifyFailure],
    )

    const addToDraft = useCallback(
        async (publicId: string, detections: { id: string }[], sourceEl?: HTMLElement | null): Promise<DraftAddResult | null> => {
            const ids = detections.map((d) => String(d.id))
            if (!ids.length) return null
            try {
                setActive(publicId)
                const res = await addItems(publicId, ids)
                celebrateAdd(sourceEl)
                return toResult(publicId, resolveTitle(publicId), res)
            } catch {
                notifyFailure("Couldn't add to the package — please try again.")
                return null
            }
        },
        [addItems, resolveTitle, setActive, celebrateAdd, notifyFailure],
    )

    const createDraftWith = useCallback(
        async (detections: { id: string }[], sourceEl?: HTMLElement | null): Promise<DraftAddResult | null> => {
            const ids = detections.map((d) => String(d.id))
            try {
                const created = await createDraft()
                if (!ids.length)
                    return {
                        publicId: created.public_id,
                        title: created.title,
                        addedIds: [],
                        addedCount: 0,
                        alreadyInDraft: 0,
                        unknownCount: 0,
                        draftCount: 0,
                    }
                const res = await addItems(created.public_id, ids)
                celebrateAdd(sourceEl)
                return toResult(created.public_id, created.title, res)
            } catch {
                notifyFailure("Couldn't start a new package — please try again.")
                return null
            }
        },
        [createDraft, addItems, celebrateAdd, notifyFailure],
    )

    const removeFromActiveDraft = useCallback(
        async (detectionId: string) => {
            if (!activeDraftId) return
            try {
                await action.mutateAsync({
                    publicId: activeDraftId,
                    action: "remove_item",
                    payload: { detectionId },
                    actorName,
                    actorEmail,
                })
            } catch {
                notifyFailure("Couldn't update the package — please try again.")
            }
        },
        [activeDraftId, action, actorName, actorEmail, notifyFailure],
    )

    const removeItemsFromDraft = useCallback(
        async (publicId: string, detectionIds: string[]) => {
            try {
                for (const detectionId of detectionIds) {
                    await action.mutateAsync({ publicId, action: "remove_item", payload: { detectionId }, actorName, actorEmail })
                }
            } catch {
                notifyFailure("Couldn't update the package — please try again.")
            }
        },
        [action, actorName, actorEmail, notifyFailure],
    )

    const discardActiveDraft = useCallback(async () => {
        const id = activeDraftId
        if (!id) return
        try {
            await action.mutateAsync({ publicId: id, action: "cancel", actorName, actorEmail })
            setActive(null)
        } catch {
            notifyFailure("Couldn't discard the draft — please try again.")
        }
    }, [activeDraftId, action, actorName, actorEmail, setActive, notifyFailure])

    const requestTitleFocus = useCallback(() => setTitleFocusNonce((n) => n + 1), [])

    const setActiveDraft = useCallback((publicId: string | null) => setActive(publicId), [setActive])

    const value = useMemo<DraftPackageContextValue>(
        () => ({
            enabled,
            canCreate,
            activeDraftId,
            activeDraft: activeDraft ?? null,
            draftItemIds,
            drafts,
            isBusy: createDraftMut.isPending || action.isPending,
            draftFor,
            addToActiveDraft,
            addToDraft,
            createDraftWith,
            setActiveDraft,
            discardActiveDraft,
            removeFromActiveDraft,
            removeItemsFromDraft,
            notify,
            requestTitleFocus,
            titleFocusNonce,
            batchBarHeight,
            setBatchBarHeight,
            registerTrayPill,
            trayPulseNonce,
        }),
        [
            enabled,
            canCreate,
            activeDraftId,
            activeDraft,
            draftItemIds,
            drafts,
            createDraftMut.isPending,
            action.isPending,
            draftFor,
            addToActiveDraft,
            addToDraft,
            createDraftWith,
            setActiveDraft,
            discardActiveDraft,
            removeFromActiveDraft,
            removeItemsFromDraft,
            notify,
            requestTitleFocus,
            titleFocusNonce,
            batchBarHeight,
            registerTrayPill,
            trayPulseNonce,
        ],
    )

    return (
        <DraftPackageContext.Provider value={value}>
            {children}
            {/* Shared top-right toast for all draft surfaces. */}
            <Toast ref={toastRef} position="top-right" />
            {canCreate && <DraftTray />}
        </DraftPackageContext.Provider>
    )
}
